import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { APIProvider, Map, useMapsLibrary } from '@vis.gl/react-google-maps';
import type { Customer, CreateCustomerForm } from '../../types';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';
const HAS_MAPS_KEY =
  Boolean(GOOGLE_MAPS_API_KEY) &&
  GOOGLE_MAPS_API_KEY !== 'your-google-maps-api-key' &&
  !GOOGLE_MAPS_API_KEY.includes('placeholder');

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  contactPerson: z.string().optional(),
  mobile: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  address: z.string().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  geofenceRadius: z.number().min(50).max(500),
});

type FormData = z.infer<typeof schema>;

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerForm) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

type PlacePrediction = google.maps.places.AutocompletePrediction;

/**
 * Uses Places AutocompleteService + MUI Autocomplete so suggestions render
 * inside the dialog (Google's .pac-container is hidden behind MUI Dialog z-index).
 */
function PlacesAutocompleteField({
  value,
  onChange,
  onPlaceSelected,
}: {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: { address: string; lat: number; lng: number }) => void;
}) {
  const places = useMapsLibrary('places');
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [helper, setHelper] = useState('Type at least 2 characters to search');
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const attributionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!places) return;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    if (!placesServiceRef.current && attributionRef.current) {
      placesServiceRef.current = new places.PlacesService(attributionRef.current);
    }
  }, [places]);

  useEffect(() => {
    if (!places) return;

    const query = inputValue.trim();
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      setHelper('Type at least 2 characters to search');
      return;
    }

    const service = new places.AutocompleteService();
    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(() => {
      service.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (predictions, status) => {
          if (cancelled) return;
          setLoading(false);

          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            setOptions([]);
            setHelper('No places found');
            return;
          }

          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            setOptions([]);
            setHelper(`Places search failed (${status}). Check Places API is enabled for your key.`);
            return;
          }

          setOptions(predictions);
          setHelper('Select a suggestion to fill address and map pin');
        },
      );
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [places, inputValue]);

  const resolvePlace = useCallback(
    (prediction: PlacePrediction) => {
      if (!places) return;

      if (!placesServiceRef.current) {
        const el = attributionRef.current ?? document.createElement('div');
        placesServiceRef.current = new places.PlacesService(el);
      }

      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address', 'geometry', 'name'],
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (place, status) => {
          // Refresh session token after a successful details call
          sessionTokenRef.current = new places.AutocompleteSessionToken();

          if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
            setHelper('Could not load place details. Try another suggestion.');
            return;
          }

          const address = place.formatted_address ?? place.name ?? prediction.description;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setInputValue(address);
          onChange(address);
          onPlaceSelected({ address, lat, lng });
          setHelper('Location set from Google Places');
        },
      );
    },
    [places, onChange, onPlaceSelected],
  );

  if (!places) {
    return (
      <TextField
        label="Search Address (Google Places)"
        fullWidth
        disabled
        value={inputValue}
        helperText="Loading Google Places…"
      />
    );
  }

  return (
    <>
      <div ref={attributionRef} style={{ display: 'none' }} />
      <Autocomplete
        freeSolo
        fullWidth
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.description
        }
        isOptionEqualToValue={(a, b) =>
          typeof a === 'string' || typeof b === 'string'
            ? a === b
            : a.place_id === b.place_id
        }
        inputValue={inputValue}
        onInputChange={(_, next, reason) => {
          setInputValue(next);
          if (reason === 'input' || reason === 'clear') {
            onChange(next);
          }
        }}
        onChange={(_, selected) => {
          if (selected && typeof selected !== 'string') {
            resolvePlace(selected);
          }
        }}
        loading={loading}
        noOptionsText={
          inputValue.trim().length < 2 ? 'Keep typing…' : 'No matching places'
        }
        componentsProps={{
          popper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.place_id}>
            <LocationOnIcon color="action" sx={{ mr: 1.5, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {option.structured_formatting?.main_text ?? option.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.structured_formatting?.secondary_text}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Address (Google Places)"
            placeholder="e.g. Bandra Kurla Complex, Mumbai"
            helperText={helper}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnIcon color="primary" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
}

function CustomerMapPicker({
  mapCenter,
  pinPosition,
  onMapClick,
}: {
  mapCenter: { lat: number; lng: number };
  pinPosition: { lat: number; lng: number } | null;
  onMapClick: (lat: number, lng: number) => void;
}) {
  return (
    <Box
      sx={{
        height: 260,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      <Map
        defaultCenter={mapCenter}
        defaultZoom={pinPosition ? 15 : 5}
        center={mapCenter}
        zoom={pinPosition ? 15 : 5}
        gestureHandling="greedy"
        disableDefaultUI={false}
        onClick={(event) => {
          const latLng = event.detail?.latLng;
          if (!latLng) return;
          onMapClick(latLng.lat, latLng.lng);
        }}
        style={{ width: '100%', height: '100%' }}
      />
      {pinPosition && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            fontSize: 28,
            lineHeight: 1,
            filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.35))',
          }}
          aria-hidden
        >
          📍
        </Box>
      )}
    </Box>
  );
}

export function CustomerForm({ customer, onSubmit, onCancel, isSubmitting }: CustomerFormProps) {
  const [mapCenter, setMapCenter] = useState(
    customer?.latitude && customer?.longitude
      ? { lat: customer.latitude, lng: customer.longitude }
      : DEFAULT_CENTER,
  );
  const [pinPosition, setPinPosition] = useState<{ lat: number; lng: number } | null>(
    customer?.latitude && customer?.longitude
      ? { lat: customer.latitude, lng: customer.longitude }
      : null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: customer?.name ?? '',
      contactPerson: customer?.contactPerson ?? '',
      mobile: customer?.mobile ?? '',
      email: customer?.email ?? '',
      address: customer?.address ?? '',
      latitude: customer?.latitude,
      longitude: customer?.longitude,
      geofenceRadius: customer?.geofence?.radius ?? 100,
    },
  });

  const latValue = watch('latitude');
  const lngValue = watch('longitude');
  const radius = watch('geofenceRadius');
  const addressValue = watch('address') ?? '';

  const applyCoordinates = useCallback((lat: number, lng: number) => {
    setValue('latitude', lat, { shouldValidate: true });
    setValue('longitude', lng, { shouldValidate: true });
    setPinPosition({ lat, lng });
    setMapCenter({ lat, lng });
  }, [setValue]);

  const handleAddressChange = useCallback(
    (next: string) => {
      setValue('address', next);
    },
    [setValue],
  );

  const handlePlaceSelected = useCallback(
    ({ address, lat, lng }: { address: string; lat: number; lng: number }) => {
      setValue('address', address);
      applyCoordinates(lat, lng);
    },
    [setValue, applyCoordinates],
  );

  const handleFormSubmit = async (data: FormData) => {
    setSubmitError(null);
    try {
      await onSubmit({
        name: data.name.trim(),
        contactPerson: data.contactPerson?.trim() || undefined,
        mobile: data.mobile?.trim() || undefined,
        email: data.email?.trim() || undefined,
        address: data.address?.trim() || undefined,
        latitude: data.latitude,
        longitude: data.longitude,
        geofenceRadius: data.geofenceRadius,
      });
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const message =
        axiosErr.response?.data?.message ||
        axiosErr.message ||
        'Failed to save customer';
      setSubmitError(message);
      throw err;
    }
  };

  return (
    <>
      <DialogTitle>{customer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Box component="form" id="customer-form" onSubmit={handleSubmit(handleFormSubmit)}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {!HAS_MAPS_KEY && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Google Maps key is not configured. You can still add a customer with a manual
              address and latitude/longitude. Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in
              <code> apps/web/.env</code> to enable Places search and the map.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('name')}
                label="Customer / Business Name *"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField {...register('contactPerson')} label="Contact Person" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField {...register('mobile')} label="Mobile Number" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('email')}
                label="Email Address"
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                Location
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {HAS_MAPS_KEY ? (
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
                  <PlacesAutocompleteField
                    value={addressValue}
                    onChange={handleAddressChange}
                    onPlaceSelected={handlePlaceSelected}
                  />
                  <Box mt={2}>
                    <CustomerMapPicker
                      mapCenter={mapCenter}
                      pinPosition={pinPosition}
                      onMapClick={applyCoordinates}
                    />
                  </Box>
                </APIProvider>
              ) : (
                <TextField
                  {...register('address')}
                  label="Address"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Enter full address"
                  helperText="Enter address manually, then set latitude and longitude below"
                />
              )}
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="latitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Latitude"
                    type="number"
                    fullWidth
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      field.onChange(Number.isNaN(val) ? undefined : val);
                      if (!Number.isNaN(val) && lngValue !== undefined) {
                        applyCoordinates(val, lngValue);
                      }
                    }}
                    error={!!errors.latitude}
                    helperText={errors.latitude?.message}
                    inputProps={{ step: 'any' }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="longitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Longitude"
                    type="number"
                    fullWidth
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      field.onChange(Number.isNaN(val) ? undefined : val);
                      if (!Number.isNaN(val) && latValue !== undefined) {
                        applyCoordinates(latValue, val);
                      }
                    }}
                    error={!!errors.longitude}
                    helperText={errors.longitude?.message}
                    inputProps={{ step: 'any' }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                Geofence
              </Typography>
              <Box sx={{ px: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Geofence Radius
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {radius ?? 100}m
                  </Typography>
                </Box>
                <Controller
                  name="geofenceRadius"
                  control={control}
                  render={({ field }) => (
                    <Slider
                      {...field}
                      min={50}
                      max={500}
                      step={10}
                      marks={[
                        { value: 50, label: '50m' },
                        { value: 100, label: '100m' },
                        { value: 200, label: '200m' },
                        { value: 300, label: '300m' },
                        { value: 500, label: '500m' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}m`}
                      onChange={(_, v) => field.onChange(v as number)}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={isSubmitting} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          type="submit"
          form="customer-form"
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Saving...' : customer ? 'Update Customer' : 'Add Customer'}
        </Button>
      </DialogActions>
    </>
  );
}
